import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ClaraLogo } from "./ClaraLogo";
import { useLang, usePath } from "../i18n";

const LINKS = [
  { key: "about", path: "/about" },
  { key: "products", path: "/grades" },
  { key: "quality", path: "/quality" },
  { key: "news", path: "/news" },
  { key: "contact", path: "/contact" },
];

const LangToggle = ({ scrolled }) => {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const switchTo = (l) => {
    if (l === lang) return;
    const rest = pathname.replace(/^\/(en|id|zh)/, "");
    navigate(`/${l}${rest}`);
  };
  const base = scrolled ? "text-clara-espresso/50" : "text-clara-cream/60";
  const active = scrolled ? "text-clara-terracotta" : "text-clara-cream";
  const options = [["en", "EN"], ["id", "ID"], ["zh", "中文"]];
  return (
    <div data-testid="lang-toggle" className="flex items-center gap-1 font-data text-xs tracking-wider">
      {options.map(([code, label], i) => (
        <span key={code} className="flex items-center gap-1">
          {i > 0 && <span className={base}>/</span>}
          <button onClick={() => switchTo(code)} data-testid={`lang-${code}`} className={`${lang === code ? active : base} hover:opacity-80 transition-opacity`}>{label}</button>
        </span>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const lp = usePath();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // Home starts over the dark hero; every other page gets the solid bar from the top.
  const solid = scrolled || !/^\/(en|id)\/?$/.test(pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-clara-cream/90 backdrop-blur-md border-b border-black/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to={lp("/")} data-testid="nav-logo" className="leading-none">
          <ClaraLogo variant={solid ? "maroon" : "cream"} className="h-11 sm:h-14" />
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.path}
              to={lp(l.path)}
              data-testid={`nav-link-${l.key}`}
              className={`text-sm font-medium hover:text-clara-terracotta transition-colors duration-300 ${solid ? "text-clara-espresso/80" : "text-clara-cream/85"}`}
            >
              {t.nav[l.key]}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <LangToggle scrolled={solid} />
          <Link
            to={lp("/contact")}
            data-testid="nav-request-sample-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-clara-terracotta px-6 py-2.5 text-sm font-semibold text-clara-cream hover:bg-clara-espresso transition-colors duration-300"
          >
            {t.nav.requestSample}
          </Link>
          <button
            className={`lg:hidden ${solid ? "text-clara-espresso" : "text-clara-cream"}`}
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            <div className="w-6 h-[2px] bg-current mb-1.5" />
            <div className="w-6 h-[2px] bg-current" />
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-clara-cream border-t border-black/10 px-6 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <Link
              key={l.path}
              to={lp(l.path)}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.key}`}
              className="text-lg font-display text-clara-espresso"
            >
              {t.nav[l.key]}
            </Link>
          ))}
        </div>
      )}
    </motion.header>
  );
};
